/**
 * Shared property name constants.
 * Ensures frontend and server use identical property names.
 */

export const ANALYTICS_PROPERTIES = {
  // Common
  TASK_ID: 'task_id',
  AGENT_SLUG: 'agent_slug',
  USER_ID: 'user_id',
  SOURCE: 'source',
  DURATION_MS: 'duration_ms',
  DURATION_SECONDS: 'duration_seconds',
  SUCCESS: 'success',

  // Task
  MESSAGE_LENGTH: 'message_length',
  MESSAGE_COUNT: 'message_count',
  HAS_ATTACHMENTS: 'has_attachments',
  TURN_COUNT: 'turn_count',
  IDLE_DURATION_SECONDS: 'idle_duration_seconds',
  ACTION: 'action',
  // Observability — end-to-end trace correlation + client-perceived TTFT
  TRACE_ID: 'trace_id',
  FIRST_TOKEN_MS: 'first_token_ms',
  SEND_PATH: 'send_path',
  FIRST_TOKEN_KIND: 'first_token_kind',

  // Agent
  FROM_SLUG: 'from_slug',
  TO_SLUG: 'to_slug',
  IN_TASK: 'in_task',
  HAS_CUSTOM_PROMPT: 'has_custom_prompt',
  DAYS_SINCE_INSTALL: 'days_since_install',

  // Tools
  TOOL_NAME: 'tool_name',
  DECISION: 'decision',
  RESPONSE_TIME_MS: 'response_time_ms',
  REASON: 'reason',

  // Tokens
  INPUT_TOKENS: 'input_tokens',
  OUTPUT_TOKENS: 'output_tokens',
  CACHE_READ_TOKENS: 'cache_read_tokens',
  TOTAL_TOKENS: 'total_tokens',
  TURN_NUMBER: 'turn_number',

  // Credits
  AMOUNT: 'amount',
  BALANCE_AFTER: 'balance_after',
  ESTIMATED_COST: 'estimated_cost',
  PAYMENT_METHOD: 'payment_method',
  CODE: 'code',
  THRESHOLD: 'threshold',
  // Purchase journey / grants (analytics-suite 2026-08-17)
  OUTCOME: 'outcome',
  GRANT_TYPE: 'grant_type',
  PLAN: 'plan',
  PERIOD: 'period',
  GATEWAY: 'gateway',
  CURRENCY: 'currency',
  AMOUNT_MINOR: 'amount_minor',
  STATUS: 'status',
  EVENT_TYPE: 'event_type',
  // Model pick
  MODEL: 'model',
  SURFACE: 'surface',

  // Files
  MIME_TYPE: 'mime_type',
  SIZE_BYTES: 'size_bytes',

  // Routines
  ROUTINE_ID: 'routine_id',
  TRIGGER_TYPE: 'trigger_type',
  HAS_CRON: 'has_cron',
  ENABLED: 'enabled',
  ERROR_TYPE: 'error_type',

  // Integration
  PROVIDER: 'provider',
  DAYS_CONNECTED: 'days_connected',

  // Navigation
  PATH: 'path',
  REFERRER: 'referrer',
  FROM_TAB: 'from_tab',
  TO_TAB: 'to_tab',
  PAGE: 'page',
  QUERY_LENGTH: 'query_length',
  CONTEXT: 'context',
  RESULT_COUNT: 'result_count',

  // Skill
  SKILL_SLUG: 'skill_slug',

  // Memory
  MEMORY_ID: 'memory_id',
  COUNT: 'count',

  // Account
  SETTING_KEY: 'setting_key',
  SECTION: 'section',

  // Onboarding
  STEP: 'step',

  // Sandbox
  SESSION_ID: 'session_id',
  BLOCKED_COMMAND: 'blocked_command',
  HOOK_NAME: 'hook_name',
  PARENT_TOOL: 'parent_tool',
  SUBAGENT_COUNT: 'subagent_count',
  FROM_IMAGE: 'from_image',
  TO_IMAGE: 'to_image',
  WAKE_DURATION_MS: 'wake_duration_ms',
  // obs-w4b — zombie wire-up props
  UPGRADE_TYPE: 'upgrade_type', // SERVER_SANDBOX_UPGRADED: 'code' | 'image'
  IDLE_MINUTES: 'idle_minutes', // SERVER_TASK_TIMED_OUT: how long the turn was stalled
} as const;

/**
 * PostHog person properties (set via identify).
 */
export const PERSON_PROPERTIES = {
  EMAIL: 'email',
  NAME: 'name',
  TOTAL_TASKS: 'total_tasks',
  TOTAL_MESSAGES_SENT: 'total_messages_sent',
  AGENTS_INSTALLED: 'agents_installed',
  SKILLS_INSTALLED: 'skills_installed',
  ROUTINES_ACTIVE: 'routines_active',
  INTEGRATIONS_CONNECTED: 'integrations_connected',
  CREDIT_BALANCE: 'credit_balance',
  PLAN: 'plan',
  FIRST_TASK_AT: 'first_task_at',
  LAST_ACTIVE_AT: 'last_active_at',
  // obs-w4b — enrichment set on socket connect (throttled 5min). first_seen =
  // users.created_at; onboarding_status = users.onboarding_status. These let
  // funnels/cohorts segment by plan, tenure and activation state.
  FIRST_SEEN: 'first_seen',
  ONBOARDING_STATUS: 'onboarding_status',
} as const;
